import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Grid, Typography, Button, Box, Slide, useTheme } from '@mui/material';
import { withTransaction } from '@elastic/apm-rum-react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import { useNavigate } from 'react-router-dom';

import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import ApplicationTabDialog from '../../components/ApplicationTabDialog';
import useOrganization from '../../hooks/UseOrganization';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import DeleteDialog from '../../components/Common/DeleteDialog';
import ChangeStatusDialog from '../../components/Common/ChangeStatusDialog';
import CustomPermissionsDialog from '../../components/CustomPermissionsDialog';
import CreateServiceDialog from '../../components/CreateServiceDialog';
import AddProductDialog from '../../components/AddProductDialog';
import {
  CommonTableCell,
  DateTimeCell,
  StatusCell,
} from '../../components/Common/CommonTableCell';
import utils from '../../utils/Utils';
import SearchBox from '../../components/Common/SearchBox';

/**
 * @function ApplicationSetting
 * @description The ApplicationSetting component displays a list of applications with options to search, add, edit, delete, and change the status of the applications.
 * @returns {JSX} renders ApplicationSetting component UI
 * @example <ApplicationSetting/>
 */
const ApplicationSetting = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const tableColumns = useMemo(
    () => [
      {
        heading: 'Application Name',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'name',
        sortType: utils.caseInsensitiveSort,
        Cell: CommonTableCell,
        width: 150,
      },
      {
        heading: 'Created At',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'created_at',
        Cell: DateTimeCell,
        width: 150,
      },
      {
        heading: 'Updated At',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'updated_at',
        Cell: DateTimeCell,
        width: 150,
      },
      {
        heading: 'Status',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'is_active',
        Cell: StatusCell,
        sortType: utils.statusSort,
        width: 150,
      },
    ],
    [utils]
  );
  const orgName = useSelector((state) => state.common.orgName);
  const {
    getApplicationList,
    updateApplication,
    ApplicationDelete,
    createServices,
  } = useOrganization();

  const [applicationList, setApplicationList] = useState([]);
  const [deleteApplication, setDeleteApplication] = useState({});
  const [editApplication, setEditApplication] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [open, setOpen] = useState(false);
  const [openServiceModal, setOpenServiceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQueryText, setSearchQueryText] = useState('');
  const permissions = useSelector((state) => state.common.permissions);
  const subadmin = useSelector((state) => state.common.subAdminBoolean);
  const [clickedProduct, setClickedProduct] = useState(null);
  const [clickedClient, setClickedClient] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (orgName) {
      setIsLoading(true);
      getApplicationList(orgName, pageIndex, pageSize, searchTerm).then(
        (resp) => {
          setApplicationList(resp?.data);
          setPageCount(resp?.max_pages);
          setTotalCount(resp?.total_count);
          setIsLoading(false);
        }
      );
    }
  }, [orgName]);

  useEffect(() => {
    if (pageIndex >= 0 && pageSize > 0 && orgName) {
      getApplicationList(orgName, pageIndex, pageSize, searchTerm).then(
        (resp) => {
          setApplicationList(resp?.data);
          setPageCount(resp?.max_pages);
          setTotalCount(resp?.total_count);
        }
      );
    }
  }, [pageIndex, pageSize, orgName]);

  useEffect(() => {
    getApplicationList(orgName, 0, pageSize, searchTerm).then((resp) => {
      setApplicationList(resp?.data);
      setPageCount(resp?.max_pages);
      setTotalCount(resp?.total_count);
    });
  }, [searchTerm]);

  /**
   * @function debouncedSave
   * @description debounce function to update the search term
   * @returns {void} sets the the searchTerm value
   * @example debouncedSave()
   */
  const debouncedSave = useCallback(
    debounce((data) => {
      setSearchTerm(data);
    }, 800),
    []
  );

  /**
   * @function searchApplicationByName
   * @description handles the search input change to filter applications by name.
   * @param {object} e - The event object from the search input field.
   * @returns {void} sets the the searchTerm value
   * @example searchApplicationByName(event)
   */

  const searchApplicationByName = useCallback(
    (e) => {
      debouncedSave(e.target.value); // Call the debounced function
    },
    [debouncedSave]
  );

  /**
   * @function handleSetSearchText
   * @description handles the input field of searchText.
   * @param {object} searchText - the current text is passed from user for searching.
   * @returns {void} sets the searchText value
   * @example handleSetSearchText(searchText)
   */
  const handleSetSearchText = (searchText) => {
    setSearchQueryText(searchText);
  };

  /**
   * @function handleDisableApplication
   * @description Toggles the activation status of the application.
   * @param {object} payload - The payload containing the updated application data.
   * @param {string} id - The ID of the application to be updated.
   * @param {number} orgName - The ID of the organization to which the application belongs.
   * @param {number} pageIndex - The page page index of current page.
   * @param {number} pageSize - The page size of current page.
   * @param {number} searchTerm - search term for filtering the data.
   * @returns {void} - updates the activation status of an application by making an API call
   * @example handleDisableApplication(payload, applicationId, orgName)
   */

  const handleDisableApplication = useCallback(
    (rowDetail, orgId, pageIndexData, pageSizeData, searchTermData) => {
      const payload = {
        is_active: !rowDetail?.is_active,
        root_url: rowDetail?.root_url,
        name: rowDetail?.name,
        redirect_url: rowDetail?.redirect_url,
      };
      updateApplication(payload, rowDetail.id, orgId).then(() => {
        getApplicationList(
          orgId,
          pageIndexData,
          pageSizeData,
          searchTermData
        ).then((resp) => {
          setApplicationList(resp?.data);
          setPageCount(resp?.max_pages);
          setTotalCount(resp?.total_count);
        });
        setShowStatusModal(false);
      });
    },
    [updateApplication, getApplicationList]
  );

  /**
   * @function onTableStateChangeMethod
   * @description helps in setting the pageIndex and pageSize
   * @param {object} pageIndex - pageIndex method of the table.
   * @returns {void} - updates the pageIndex and pageSize.
   * @example onTableStateChangeMethod(pageIndex)
   */
  const onTableStateChangeMethod = useCallback(
    (pageIndexData) => {
      setPageIndex(pageIndexData?.pageIndex);
      setPageSize(pageIndexData?.pageSize);
    },
    [setPageIndex, setPageSize]
  );
  const [showCustomPermissionModal, setShowCustomPermissionModal] =
    useState(false);
  const menus = useMemo(
    () => [
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        isMenuDisabled: () => Boolean(subadmin),
        onClick: ({ props }) => {
          setEditApplication(props?.original);
          setMode('edit');
          setOpen(true);
        },
      },
      {
        label: 'Delete',
        id: 'delete',
        icon: DeleteIcon,
        isMenuDisabled: () => Boolean(subadmin),
        onClick: ({ props }) => {
          setShowDeleteModal(true);
          setDeleteApplication(props?.original);
        },
      },
      {
        label: 'Change Status',
        id: 'disable',
        icon: ChangeCircleIcon,
        isMenuDisabled: () => Boolean(subadmin),
        onClick: ({ props }) => {
          setShowStatusModal(true);
          setDeleteApplication(props?.original);
        },
      },
      {
        label: 'Custom Permissions',
        id: 'custom_permissions',
        icon: AppRegistrationIcon,
        onClick: ({ props }) => {
          setShowCustomPermissionModal(true);
          setClickedProduct(props?.original?.product);
          setClickedClient(props?.original);
        },
      },
      {
        label: 'Theme Configuration',
        id: 'theme_configuration',
        icon: ColorLensIcon,
        onClick: ({ props }) => {
          navigate('theme', {
            state: {
              orgName,
              productDetails: props?.original,
            },
          });
        },
      },
      {
        label: 'File Configuration',
        id: 'file_configuration',
        icon: DescriptionIcon,
        isMenuDisabled: () => Boolean(subadmin),
        onClick: ({ props }) => {
          navigate('file', {
            state: {
              orgName,
              productDetails: props?.original,
            },
          });
        },
      },
      {
        label: 'Create Services',
        id: 'create_services',
        icon: MiscellaneousServicesIcon,
        isMenuDisabled: () => Boolean(subadmin),
        onClick: ({ props }) => {
          setClickedProduct(props?.original?.id);
          setOpenServiceModal(true);
        },
      },
    ],
    [pageSize, pageIndex, searchTerm, subadmin, permissions, orgName]
  );

  /**
   * @function handleDeleteApplication()
   * @description This function helps in deleting the application
   * @param {number} orgName - The ID of the organization to delete.
   * @param {number} pageIndex - The page page index of current page.
   * @param {number} pageSize - The page size of current page.
   * @param {number} searchTerm - search term for filtering the data.
   * @returns {void}
   * @example
   */
  const handleDeleteApplication = useCallback(
    (id, orgId, pageIndexData, pageSizeData, searchTermData) => {
      ApplicationDelete(id, orgId).then(() => {
        getApplicationList(
          orgId,
          pageIndexData,
          pageSizeData,
          searchTermData
        ).then((resp) => {
          setApplicationList(resp?.data);
          setPageCount(resp?.max_pages);
          setTotalCount(resp?.total_count);
        });
      });
      setShowDeleteModal(false);
    },
    [
      ApplicationDelete,
      getApplicationList,
      setShowDeleteModal,
      setApplicationList,
    ]
  );

  const handleCloseStatusModal = useCallback(() => {
    setShowStatusModal(false);
  }, [setShowStatusModal]);

  const handleChangeStatus = useCallback(() => {
    handleDisableApplication(
      deleteApplication,
      orgName,
      pageIndex,
      pageSize,
      searchTerm
    );
  }, [
    handleDisableApplication,
    deleteApplication,
    orgName,
    pageIndex,
    pageSize,
    searchTerm,
  ]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box
          margin="20px"
          display="flex"
          flexDirection="column"
          overflow="auto"
        >
          <Typography variant="h3">Application</Typography>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="flex-end"
            mt={2}
          >
            <Box width="200px">
              <SearchBox
                placeholder="Search"
                variant="filled"
                value={searchQueryText}
                onChange={searchApplicationByName}
                onInput={(e) => handleSetSearchText(e.target.value)}
                aria-label="Search sub-admin"
              />
            </Box>
            <Button
              title={
                subadmin
                  ? 'You are not authorized to access this!'
                  : 'Create Application'
              }
              variant="contained"
              disabled={subadmin}
              onClick={() => setOpen(true)}
            >
              + Create Application
            </Button>
          </Box>
          <Box flexGrow={1} display="flex" flexDirection="column">
            {isLoading ? (
              <Box
                flexGrow={1}
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                height="20vh"
              >
                <Typography variant="h6" textAlign="center" sx={{ pt: '10px' }}>
                  Loading...
                </Typography>
              </Box>
            ) : (
              <Slide in={true} direction="up" mountOnEnter timeout={500}>
                {applicationList?.length > 0 ? (
                  <Box pt={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <ReactMuiTableListView
                      data={applicationList}
                      columns={tableColumns}
                      onTableStateChange={onTableStateChangeMethod}
                      getHeaderProps={() => ({
                        style: {
                          display: 'flex',
                          alignItems: 'center',
                        },
                      })}
                      getRowProps={() => ({
                        style: {
                          position: 'relative',
                        },
                      })}
                      enableRowSelection={true}
                      pageCount={Number(pageCount)}
                      dataLength={Number(totalCount)}
                      enablePagination={true}
                      initialPageSize={5}
                      rowsPerPageOptions={[5, 10, 15, 20]}
                      rowLevelRightClickMenus={menus}
                      enableManualFilter={true}
                      enableControlledPagination={true}
                      showPaginationAction={false}
                      skipPageReset={true}
                      searchTerm={searchTerm}
                    />
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                    overflow="auto"
                    minHeight="300px"
                    height="calc(100vh - 438px)"
                  >
                    {searchTerm ? (
                      <NoDataImage
                        style={{ color: theme.palette.primary.main }}
                      />
                    ) : (
                      <NoDataCreated
                        style={{ color: theme.palette.primary.main }}
                      />
                    )}
                    <Typography
                      variant="subtitle1"
                      textAlign="center"
                      sx={{ pt: '10px' }}
                    >
                      {searchTerm
                        ? 'No matching result found.'
                        : 'No applications found. Please create an application to proceed.'}
                    </Typography>
                  </Box>
                )}
              </Slide>
            )}
          </Box>
        </Box>
      </Grid>
      {showProductDialog && (
        <AddProductDialog
          open={showProductDialog}
          handleClose={() => setShowProductDialog(false)}
        />
      )}
      <DeleteDialog
        open={showDeleteModal}
        onCloseClick={() => setShowDeleteModal(false)}
        onDeleteClick={() =>
          handleDeleteApplication(
            deleteApplication?.id,
            orgName,
            pageIndex,
            pageSize,
            searchTerm
          )
        }
        confirmationMsg={`Are you sure you want to delete "${deleteApplication?.name}" application ?`}
        resourceName="Application"
      />
      <ChangeStatusDialog
        open={showStatusModal}
        onCloseClick={handleCloseStatusModal}
        onChangeClick={handleChangeStatus}
        confirmationMsg={`Are you sure you want to change the status of "${deleteApplication?.name}" to "${deleteApplication?.is_active ? 'Inactive' : 'Active'}"?`}
      />
      {open && (
        <ApplicationTabDialog
          open={open}
          setOpen={setOpen}
          mode={mode}
          editApplication={editApplication}
          setMode={setMode}
          setApplicationList={setApplicationList}
          pageIndex={pageIndex}
          pageSize={pageSize}
          searchTerm={searchTerm}
          setPageCount={setPageCount}
          setTotalCount={setTotalCount}
        />
      )}
      {showCustomPermissionModal && (
        <CustomPermissionsDialog
          orgId={orgName}
          open={showCustomPermissionModal}
          handleClose={() => setShowCustomPermissionModal(false)}
          product={clickedProduct}
          client={clickedClient}
        />
      )}
      {openServiceModal && (
        <CreateServiceDialog
          open={openServiceModal}
          setOpen={setOpenServiceModal}
          createService={createServices}
          orgName={orgName}
          product={clickedProduct}
        />
      )}
    </Grid>
  );
};

export default withTransaction(
  'ApplicationSetting',
  'component'
)(ApplicationSetting);
